import { ANNOTATION_COLORS, type AnnotationColor } from "@lancer-vtt/domain";
import {
  faArrowPointer,
  faBolt,
  faChessPawn,
  faClockRotateLeft,
  faHourglassHalf,
  faPause,
  faDrawPolygon,
  faEraser,
  faMagnifyingGlass,
  faPen,
  faPenRuler,
  faRotateLeft,
  faRotateRight,
  faSignature,
  faRuler,
  faSliders,
  faXmark,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import type { ComponentType, ReactNode } from "react";
import { Icon } from "../../../lib/Icon";
import { tipAttrs, type TipAttrs } from "../../../lib/tooltip";
import type { DrawTool } from "../combatStage/types";
import type { PenMode } from "./penPrefs";
import { BUILD_TOOLS, type BuildContext } from "./BuildTools";
import { viewRowsFor, type ViewContext } from "./viewRows";

/**
 * **The board tool rail** (`decisions/0191`, `board-tool-rail` Phase 3): a column of tools on the
 * stage's right edge, for every viewer in both modes. It is an overlay inside `.combat-stage-wrap`,
 * mounted through `CombatStage`'s `overlay` slot like the command menu, so it costs the board no
 * width (call (c)). The armed tool's options open in a strip beside it, leftward (call (b)), and close
 * with the tool.
 *
 * Tools are entries in {@link RAIL_TOOLS}. EDIT adds the
 * BUILD section (`BuildTools.tsx`), whose larger options open in a panel beside the rail rather than
 * a strip. Each entry reads and changes the armed state through {@link RailContext}, so the rail holds
 * no state of its own. Arming goes through the same setters the rest of the board uses, and the
 * one-armed-thing seam (`armedSeam.ts`, layer 3 of `decisions/0189`) disarms everything else.
 *
 * Since COMBAT's top bar retired (`decisions/0203` point 9, `website-shell` 4.3) the rail also holds
 * that bar's controls, in sections top to bottom: **TABLE** (undo/redo, everyone), **VIEW** (the
 * G/O/K toggles, `viewRows.ts`, everyone), **TOOLS**, **BUILD** (GM, EDIT), **SCENE** (GM: the UNITS
 * and Map setup panels) and **GM** (Edit map, Undo commit and END COMBAT). TABLE, VIEW, SCENE and GM
 * are not armable tools: each shows only when `CombatApp` hands the rail its context
 * ({@link RailTable}, `ViewContext`, {@link RailScene}, {@link RailGm}).
 */

/** What a rail tool can read and change. `CombatApp` builds it from the board's armed state. */
export type RailContext = {
  drawTool: DrawTool;
  pickDrawTool: (d: DrawTool) => void;
  drawColor: AnnotationColor;
  setDrawColor: (c: AnnotationColor) => void;
  /** The Pen's last-used mode, which D arms (`penPrefs.ts`). */
  penMode: PenMode;
  /** Whether a BUILD brush or pointer tool is armed. */
  terrainArmed: boolean;
  disarmTerrain: () => void;
  /** Whether the Measure tool is armed (`useMeasureTool`). */
  measureArmed: boolean;
  setMeasureArmed: (armed: boolean) => void;
  /** Whether the Inspect tool is armed (`useInspectTool`). */
  inspectArmed: boolean;
  setInspectArmed: (armed: boolean) => void;
  build: BuildContext;
  /** TABLE: the encounter's one cross-mode undo stack. Absent ⇒ no TABLE section. */
  table?: RailTable;
  /** VIEW: the viewer's display toggles. Absent ⇒ no VIEW section. */
  view?: ViewContext;
  /** SCENE: the GM's panels for this encounter. Absent (a player) ⇒ no SCENE section. */
  scene?: RailScene;
  /** GM: the GM's encounter controls. Absent (a player) ⇒ no GM section. */
  gm?: RailGm;
};

/** A SCENE panel: who's in the fight, or the board's setup. */
export type ScenePanel = "units" | "map-setup";

/**
 * The SCENE section (`decisions/0203` point 5, amended 2026-10-04): the panels that edit this
 * encounter rather than the app, docked beside the rail. One is open at a time, in either mode.
 */
export type RailScene = {
  open: ScenePanel | null;
  toggle: (panel: ScenePanel) => void;
  /** The open panel's content, which `CombatApp` renders since it owns the encounter state. */
  body: ReactNode;
};

/** The SCENE section's buttons, in rail order. */
export const SCENE_PANELS: readonly { panel: ScenePanel; label: string; icon: IconDefinition; hint: string }[] = [
  { panel: "units", label: "Units", icon: faChessPawn, hint: "who's in the fight; add mechs, pilots and enemies" },
  { panel: "map-setup", label: "Map setup", icon: faSliders, hint: "board size, environment, ground, disclosure" },
];

/** The TABLE section's undo/redo: availability, the next command's label, and the presses. */
export type RailTable = {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  undo: () => void;
  redo: () => void;
};

/**
 * The GM section: the Edit map toggle (until `gm-single-mode` removes it), Undo commit while the
 * encounter's commit can be walked back, and END COMBAT while this encounter is the table's scene.
 */
export type RailGm = {
  mode: "edit" | "play";
  setMode: (mode: "edit" | "play") => void;
  /** Present while the encounter's last commit can be undone. */
  undoCommit?: () => void;
  /** Present while this encounter is the table's scene (a preview has none). Opens the END COMBAT panel. */
  endCombat?: () => void;
  /** Present once the fight is over while its wear is uncommitted: opens the panel as COMMIT WEAR,
   *  in END COMBAT's place, so an end without a commit never strands the wear. */
  commitWear?: () => void;
  /** PLAY: the sticky GM OVERRIDE switch (`decisions/0209`) — rules off for the GM's attacks. */
  override?: { on: boolean; toggle: () => void };
  /**
   * The chain holds (`chained-resolution-sequence` 3.1): **hold next** arms a hold before the next
   * resolution every board plays; **auto-hold** pauses before structure and overheat checks, and
   * the GM can turn it off for this encounter.
   */
  holds?: { holdNext: boolean; toggleHoldNext: () => void; autoHold: boolean; toggleAutoHold: () => void };
};

/** A rail section of armable tools: the annotation tools for everyone, and BUILD (EDIT only). */
export type RailSection = "tools" | "build";

export type RailTool = {
  id: string;
  label: string;
  icon: IconDefinition;
  /** The bare key that arms it, lower-case (the root listener in `useCombatKeyboard.ts`). */
  key: string;
  /**
   * `"hold"`: the key is a hold gesture of its own (hold-M measures), so a tap never arms the tool
   * and {@link railToolForKey} skips it. One key doing both would disarm an aim on a short hold
   * (`decisions/0189` item 5).
   */
  keyMode?: "hold";
  /** Further bare keys, each with its own press (L arms the Pen in Polygon mode). */
  extraKeys?: readonly { key: string; press: (ctx: RailContext) => void }[];
  /** What the button's tooltip says under the label and key. */
  hint: string;
  section: RailSection;
  modes: readonly ("edit" | "play")[];
  isArmed: (ctx: RailContext) => boolean;
  /** Arm it, or disarm it when it is already armed. */
  press: (ctx: RailContext) => void;
  /** The options shown beside the tool while it is armed. */
  Options?: ComponentType<{ ctx: RailContext }>;
  /** A one-row strip level with the tool (the default), or a taller panel from the rail's top. */
  optionsAs?: "strip" | "panel";
};

const BOTH_MODES = ["edit", "play"] as const;

/** The Pen's two modes, as the options strip offers them. */
export const PEN_MODES: readonly { mode: PenMode; label: string; key: string; icon: IconDefinition; hint: string }[] = [
  { mode: "freehand", label: "Freehand", key: "D", icon: faSignature, hint: "drag to sketch" },
  { mode: "polyline", label: "Polygon", key: "L", icon: faDrawPolygon, hint: "click each point; double-click or Enter finishes, Esc drops it" },
];

/** Whether the Pen is armed, in either mode. */
function penArmed(ctx: RailContext): boolean {
  return ctx.drawTool === "freehand" || ctx.drawTool === "polyline";
}

/**
 * The Pen's options strip: its mode (Freehand or Polygon), then the four {@link ANNOTATION_COLORS}
 * the next stroke uses. Picking a mode arms it; both choices are remembered per viewer.
 */
function PenOptions({ ctx }: { ctx: RailContext }) {
  return (
    <div className="combat-tool-strip" role="group" aria-label="pen options">
      <div className="combat-pen-modes" role="group" aria-label="pen mode">
        {PEN_MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={`combat-pen-mode${ctx.drawTool === m.mode ? " is-armed" : ""}`}
            data-pen-mode={m.mode}
            aria-pressed={ctx.drawTool === m.mode}
            aria-label={m.label}
            {...tipAttrs({ label: m.label, key: m.key, hint: m.hint })}
            onClick={() => ctx.pickDrawTool(m.mode)}
          >
            <Icon icon={m.icon} />
          </button>
        ))}
      </div>
      <span className="combat-tool-strip-rule" aria-hidden />
      {ANNOTATION_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={`combat-draw-swatch combat-draw-swatch-${c}${ctx.drawColor === c ? " combat-draw-swatch-on" : ""}`}
          aria-pressed={ctx.drawColor === c}
          aria-label={`${c} stroke`}
          {...tipAttrs({ label: c, hint: "the next stroke's colour" })}
          onClick={() => ctx.setDrawColor(c)}
        />
      ))}
    </div>
  );
}

/** The rail's tools, top to bottom. */
export const RAIL_TOOLS: readonly RailTool[] = [
  {
    id: "select",
    label: "Select",
    icon: faArrowPointer,
    key: "v",
    hint: "pick and move units; puts down every other tool",
    section: "tools",
    modes: BOTH_MODES,
    isArmed: (ctx) => ctx.drawTool === null && !ctx.terrainArmed && !ctx.measureArmed && !ctx.inspectArmed,
    press: (ctx) => {
      ctx.pickDrawTool(null);
      if (ctx.terrainArmed) ctx.disarmTerrain();
      ctx.setMeasureArmed(false);
      ctx.setInspectArmed(false);
    },
  },
  {
    id: "measure",
    label: "Measure",
    icon: faRuler,
    key: "m",
    keyMode: "hold",
    hint: "measure distance in spaces: press a cell or unit, then drag or click the end",
    section: "tools",
    modes: BOTH_MODES,
    isArmed: (ctx) => ctx.measureArmed,
    press: (ctx) => ctx.setMeasureArmed(!ctx.measureArmed),
  },
  {
    id: "inspect",
    label: "Inspect",
    icon: faMagnifyingGlass,
    key: "i",
    hint: "read a cell: its tile, who stands there, its zones, and its terrain groups outlined; tap on touch",
    section: "tools",
    modes: BOTH_MODES,
    isArmed: (ctx) => ctx.inspectArmed,
    press: (ctx) => ctx.setInspectArmed(!ctx.inspectArmed),
  },
  {
    id: "pen",
    label: "Pen",
    icon: faPen,
    key: "d",
    hint: "draw on the board, freehand or polygon (L), in a colour",
    section: "tools",
    modes: BOTH_MODES,
    isArmed: penArmed,
    // D re-arms the last-used mode, or puts the Pen down.
    press: (ctx) => ctx.pickDrawTool(penArmed(ctx) ? null : ctx.penMode),
    // L arms Polygon, or puts the Pen down when Polygon is already armed.
    extraKeys: [{ key: "l", press: (ctx) => ctx.pickDrawTool(ctx.drawTool === "polyline" ? null : "polyline") }],
    Options: PenOptions,
  },
  {
    id: "erase",
    label: "Erase",
    icon: faEraser,
    key: "e",
    hint: "remove lines and sketches: drag across them",
    section: "tools",
    modes: BOTH_MODES,
    isArmed: (ctx) => ctx.drawTool === "eraser",
    press: (ctx) => ctx.pickDrawTool(ctx.drawTool === "eraser" ? null : "eraser"),
  },
  ...BUILD_TOOLS,
];

/** The rail tools shown in `mode`. */
export function railToolsFor(mode: "edit" | "play", tools: readonly RailTool[] = RAIL_TOOLS): RailTool[] {
  return tools.filter((t) => t.modes.includes(mode));
}

/** What a bare key press does in `mode`, if anything (case-insensitive): its tool, and the press it makes. A hold key does nothing. */
export function railKeyAction(key: string, mode: "edit" | "play", tools: readonly RailTool[] = RAIL_TOOLS): { tool: RailTool; press: (ctx: RailContext) => void } | undefined {
  const k = key.toLowerCase();
  for (const t of railToolsFor(mode, tools)) {
    if (t.key === k && t.keyMode !== "hold") return { tool: t, press: t.press };
    const extra = t.extraKeys?.find((x) => x.key === k);
    if (extra) return { tool: t, press: extra.press };
  }
  return undefined;
}

/** The tool a bare key press reaches in `mode`, if any. */
export function railToolForKey(key: string, mode: "edit" | "play", tools: readonly RailTool[] = RAIL_TOOLS): RailTool | undefined {
  return railKeyAction(key, mode, tools)?.tool;
}

/** Every bare key the rail answers, its extra keys included. */
export function railKeys(tools: readonly RailTool[] = RAIL_TOOLS): string[] {
  return tools.flatMap((t) => [t.key, ...(t.extraKeys ?? []).map((x) => x.key)]);
}

/** The button's tooltip (`lib/tooltip.ts`): the label, the key (`L`, or `hold M` for a hold key) and the hint, opening leftward off the rail. */
export function railToolTip(t: RailTool): TipAttrs {
  return tipAttrs({ label: t.label, key: `${t.keyMode === "hold" ? "hold " : ""}${t.key.toUpperCase()}`, hint: t.hint, side: "left" });
}

/** A section's label, above its first control. */
function SectionLabel({ children }: { children: string }) {
  return (
    <div className="combat-tool-section-label" aria-hidden>
      {children}
    </div>
  );
}

/** TABLE: undo and redo, labelled from the broadcast `undoLabel`/`redoLabel` so neither is blind. */
function TableSection({ table, disabled }: { table: RailTable; disabled: boolean }) {
  const undo = table.undoLabel ? `Undo ${table.undoLabel}` : "Undo";
  const redo = table.redoLabel ? `Redo ${table.redoLabel}` : "Redo";
  return (
    <div className="combat-tool-section" data-section="table" role="group" aria-label="undo and redo">
      <SectionLabel>TABLE</SectionLabel>
      <button
        type="button"
        className="combat-tool-btn"
        disabled={disabled || !table.canUndo}
        aria-label={undo}
        {...tipAttrs({ label: undo, key: "Ctrl/⌘+Z", hint: table.canUndo ? undefined : "nothing to undo", side: "left" })}
        onClick={table.undo}
      >
        <Icon icon={faRotateLeft} className="combat-tool-glyph" />
      </button>
      <button
        type="button"
        className="combat-tool-btn"
        disabled={disabled || !table.canRedo}
        aria-label={redo}
        {...tipAttrs({ label: redo, key: "Ctrl/⌘+Shift+Z", hint: table.canRedo ? undefined : "nothing to redo", side: "left" })}
        onClick={table.redo}
      >
        <Icon icon={faRotateRight} className="combat-tool-glyph" />
      </button>
    </div>
  );
}

/** VIEW: one toggle button per row (`viewRows.ts`), pressed while on. They touch nothing on the server, so they never disable. */
function ViewSection({ mode, view }: { mode: "edit" | "play"; view: ViewContext }) {
  return (
    <div className="combat-tool-section" data-section="view" role="group" aria-label="view">
      <SectionLabel>VIEW</SectionLabel>
      {viewRowsFor(mode).map((r) => {
        const t = r.toggleOf(view);
        return (
          <button
            key={r.id}
            type="button"
            className={`combat-tool-btn${t.on ? " is-armed" : ""}`}
            data-view-row={r.id}
            aria-pressed={t.on}
            aria-label={r.label}
            {...tipAttrs({ label: r.label, key: r.key.toUpperCase(), hint: t.on ? "on" : "off", side: "left" })}
            onClick={t.toggle}
          >
            <Icon icon={r.icon} className="combat-tool-glyph" />
            <span className="combat-tool-key" aria-hidden>
              {r.key.toUpperCase()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** SCENE: one toggle per {@link SCENE_PANELS} entry, pressed while its panel is open. */
function SceneSection({ scene }: { scene: RailScene }) {
  return (
    <div className="combat-tool-section" data-section="scene" role="group" aria-label="scene">
      <SectionLabel>SCENE</SectionLabel>
      {SCENE_PANELS.map((p) => {
        const on = scene.open === p.panel;
        return (
          <button
            key={p.panel}
            type="button"
            className={`combat-tool-btn${on ? " is-armed" : ""}`}
            data-scene-panel={p.panel}
            aria-pressed={on}
            aria-label={p.label}
            {...tipAttrs({ label: p.label, hint: p.hint, side: "left" })}
            onClick={() => scene.toggle(p.panel)}
          >
            <Icon icon={p.icon} className="combat-tool-glyph" />
          </button>
        );
      })}
    </div>
  );
}

/** The open SCENE panel, docked beside the rail where a BUILD tool's panel opens. */
function ScenePanelDock({ scene }: { scene: RailScene }) {
  const meta = SCENE_PANELS.find((p) => p.panel === scene.open);
  if (!meta) return null;
  return (
    <aside className="combat-tool-strip combat-tool-panel combat-scene-panel" data-scene-panel={meta.panel} aria-label={meta.label}>
      <div className="combat-tool-panel-head combat-scene-panel-head">
        <span>{meta.label.toUpperCase()}</span>
        <button type="button" className="combat-scene-panel-close" aria-label={`Close ${meta.label}`} onClick={() => scene.toggle(meta.panel)}>
          <Icon icon={faXmark} />
        </button>
      </div>
      <div className="combat-scene-panel-body">{scene.body}</div>
    </aside>
  );
}

/** The GM's mode toggle's accessible name, in both states (`aria-pressed` carries which). */
export const EDIT_MAP_LABEL = "Edit map";
/** The GM OVERRIDE switch's accessible name, in both states (`aria-pressed` carries which). */
export const GM_OVERRIDE_LABEL = "GM override";

/**
 * GM: the Edit map toggle, pressed while in EDIT (pressing it again returns to PLAY), then Undo
 * commit and END COMBAT (at the rail's foot, behind `EndCombatPanel`'s confirm), which becomes COMMIT
 * WEAR once the fight is over and uncommitted.
 */
function GmSection({ gm }: { gm: RailGm }) {
  const editing = gm.mode === "edit";
  return (
    <div className="combat-tool-section" data-section="gm">
      <SectionLabel>GM</SectionLabel>
      <button
        type="button"
        className={`combat-tool-btn${editing ? " is-armed" : ""}`}
        data-board-mode={gm.mode}
        aria-pressed={editing}
        aria-label={EDIT_MAP_LABEL}
        {...tipAttrs(editing ? { label: "Done editing", hint: "back to play", side: "left" } : { label: EDIT_MAP_LABEL, hint: "author the map; place freely", side: "left" })}
        onClick={() => gm.setMode(editing ? "play" : "edit")}
      >
        <Icon icon={faPenRuler} className="combat-tool-glyph" />
      </button>
      {gm.override && (
        <button
          type="button"
          className={`combat-tool-btn combat-tool-override${gm.override.on ? " is-armed" : ""}`}
          aria-pressed={gm.override.on}
          aria-label={GM_OVERRIDE_LABEL}
          {...tipAttrs(gm.override.on ? { label: "Override on", hint: "click to put the rules back", side: "left" } : { label: GM_OVERRIDE_LABEL, hint: "fire any weapon, any unit, any time, at anyone", side: "left" })}
          onClick={gm.override.toggle}
        >
          <Icon icon={faBolt} className="combat-tool-glyph" />
        </button>
      )}
      {/* PLAY only: holds are about playing a fight back, and EDIT's rail is the longest already. */}
      {gm.holds && !editing && (
        <>
          <button
            type="button"
            className={`combat-tool-btn${gm.holds.holdNext ? " is-armed" : ""}`}
            aria-pressed={gm.holds.holdNext}
            aria-label="Hold next"
            {...tipAttrs({ label: "Hold next", hint: "every board pauses before the next roll until you press Roll it", side: "left" })}
            onClick={gm.holds.toggleHoldNext}
          >
            <Icon icon={faPause} className="combat-tool-glyph" />
          </button>
          <button
            type="button"
            className={`combat-tool-btn${gm.holds.autoHold ? " is-armed" : ""}`}
            aria-pressed={gm.holds.autoHold}
            aria-label="Auto-hold"
            {...tipAttrs({ label: gm.holds.autoHold ? "Auto-hold on" : "Auto-hold off", hint: "pause before every structure and overheat check, this encounter", side: "left" })}
            onClick={gm.holds.toggleAutoHold}
          >
            <Icon icon={faHourglassHalf} className="combat-tool-glyph" />
          </button>
        </>
      )}
      {gm.undoCommit && (
        <button
          type="button"
          className="combat-tool-btn"
          aria-label="Undo commit"
          {...tipAttrs({ label: "Undo commit", hint: "the pilot sheets go back to their wear before this encounter's commit", side: "left" })}
          onClick={gm.undoCommit}
        >
          <Icon icon={faClockRotateLeft} className="combat-tool-glyph" />
        </button>
      )}
      {gm.endCombat && (
        <button
          type="button"
          className="combat-tool-btn combat-tool-end"
          aria-label="End combat"
          {...tipAttrs({ label: "End combat", hint: "commit the fight's wear to the roster; the table goes to Narrative", side: "left" })}
          onClick={gm.endCombat}
        >
          END
        </button>
      )}
      {gm.commitWear && (
        <button
          type="button"
          className="combat-tool-btn combat-tool-end"
          aria-label="Commit wear"
          {...tipAttrs({ label: "Commit wear", hint: "the fight is over: write its wear to the pilot sheets", side: "left" })}
          onClick={gm.commitWear}
        >
          WEAR
        </button>
      )}
    </div>
  );
}

/**
 * The rail itself, in sections: TABLE, VIEW, the armable tools (TOOLS, then BUILD in EDIT) with the
 * armed tool's options beside them, SCENE, and GM. An open SCENE panel and a BUILD tool's panel
 * share the spot beside the rail: the SCENE panel shows while it is open, and `CombatApp` closes it
 * when a BUILD tool is picked.
 */
export function BoardToolRail({ mode, ctx, disabled }: { mode: "edit" | "play"; ctx: RailContext; disabled: boolean }) {
  const tools = railToolsFor(mode);
  // An open SCENE panel takes the spot a BUILD tool's panel opens in.
  const panelTool = ctx.scene?.open ? undefined : tools.find((t) => t.optionsAs === "panel" && t.Options && t.isArmed(ctx));
  const Panel = panelTool?.Options;
  return (
    <>
      <nav className="combat-tool-rail" aria-label="board tools">
        {ctx.table && <TableSection table={ctx.table} disabled={disabled} />}
        {ctx.view && <ViewSection mode={mode} view={ctx.view} />}
        {tools.map((t, i) => {
          const armed = t.isArmed(ctx);
          const Strip = t.optionsAs === "panel" ? undefined : t.Options;
          const opensSection = i === 0 || tools[i - 1]!.section !== t.section;
          return (
            <div key={t.id} className="combat-tool-slot" data-tool-id={t.id}>
              {opensSection && <SectionLabel>{t.section.toUpperCase()}</SectionLabel>}
              <button
                type="button"
                className={`combat-tool-btn${armed ? " is-armed" : ""}`}
                aria-pressed={armed}
                aria-label={t.label}
                {...railToolTip(t)}
                disabled={disabled}
                onClick={() => t.press(ctx)}
              >
                <Icon icon={t.icon} className="combat-tool-glyph" />
                <span className="combat-tool-key" aria-hidden>
                  {t.key.toUpperCase()}
                </span>
              </button>
              {armed && Strip && <Strip ctx={ctx} />}
            </div>
          );
        })}
        {ctx.scene && <SceneSection scene={ctx.scene} />}
        {ctx.gm && <GmSection gm={ctx.gm} />}
      </nav>
      {ctx.scene && <ScenePanelDock scene={ctx.scene} />}
      {panelTool && Panel && (
        <aside className="combat-tool-strip combat-tool-panel" data-panel-for={panelTool.id} aria-label={`${panelTool.label} options`}>
          <div className="combat-tool-panel-head">{panelTool.label.toUpperCase()}</div>
          <div className="combat-tool-panel-body">
            <Panel ctx={ctx} />
          </div>
        </aside>
      )}
    </>
  );
}
